import { DateTime, Interval } from 'luxon';
// NOTE: Yukon and a portion of Saskatchewan do not observe daylight saving time
const timezoneMapping = {
    EST: 'America/Toronto',
    CST: 'America/Winnipeg',
    MST: 'America/Edmonton',
    PST: 'America/Vancouver',
    AST: 'America/Halifax',
    NST: 'America/St_Johns',
};
const isOpenDealerHour = (dealerHour) => !!(dealerHour && dealerHour.start && dealerHour.end);
// convert DayOfWeek in format of Sun - Sat : 0 - 6 to Mon - Sun : 1 - 7
const convertDayOfWeek = (dayOfWeek) => (dayOfWeek === 0 ? 7 : dayOfWeek);
const getDealerHourByDayOfWeek = (hour, dayOfWeek) => 
// dealer hours day of week in the format of Sun - Sat : 0 - 6
dayOfWeek === 7 ? hour.dayOfWeek === 0 : dayOfWeek === hour.dayOfWeek;
const getNextOpenDealerHour = (todayDealerHour, dealerHours) => {
    let nextOpenDealerHour = null;
    let day = todayDealerHour.dayOfWeek;
    let hasLoopedEntireWeek = false;
    while (!hasLoopedEntireWeek) {
        // move to next day
        day = day === 6 ? 0 : day + 1;
        const nextDealerHour = dealerHours[day];
        if (isOpenDealerHour(nextDealerHour)) {
            // when next open hour is found, exit loop
            nextOpenDealerHour = nextDealerHour;
            break;
        }
        // check if has looped back to the starting day
        hasLoopedEntireWeek = day === todayDealerHour.dayOfWeek;
    }
    return nextOpenDealerHour;
};
const getTimeInDisplayFormat = (dateTime, locale) => {
    // convert 24 hour format to 12 hour format (used only in english locale)
    const enTime = dateTime.hour > 12 ? dateTime.hour - 12 : dateTime.hour;
    return {
        // add 0 padding to minute if necessary
        time: `${locale === 'fr-CA' ? dateTime.hour : enTime}:${dateTime.minute.toString().padStart(2, '0')}`,
        isAM: dateTime.hour === 24 || dateTime.hour < 12,
    };
};
const getDealerStatus = (dealerHours, dealerTimezone, locale) => {
    // convert user's local time to dealer's time zone for comparison
    const now = DateTime.now().setZone(timezoneMapping[dealerTimezone]).setLocale(locale);
    // day of week in the format of Mon - Sun : 1 - 7
    const dayOfWeek = now.weekday;
    // dealer operating hour in the same day
    const dealerHour = dealerHours.find(hour => getDealerHourByDayOfWeek(hour, dayOfWeek));
    if (!dealerHour)
        return null;
    // dealer is scheduled to open today
    if (isOpenDealerHour(dealerHour)) {
        const open = DateTime.fromISO(dealerHour.start, { zone: timezoneMapping[dealerTimezone], locale });
        const close = DateTime.fromISO(dealerHour.end, { zone: timezoneMapping[dealerTimezone], locale });
        const openingHours = Interval.fromDateTimes(open, close);
        const isDealerOpen = openingHours.contains(now);
        // dealer is open now
        if (isDealerOpen) {
            return {
                isDealerOpen,
                closingTime: getTimeInDisplayFormat(close, locale),
            };
        }
        // dealer will open in one or more days
        if (openingHours.isBefore(now)) {
            const nextOpenDealerHour = getNextOpenDealerHour(dealerHour, dealerHours);
            if (!nextOpenDealerHour)
                return null;
            const nextOpen = DateTime.fromISO(nextOpenDealerHour.start, { zone: dealerTimezone, locale });
            return {
                isDealerOpen,
                nextOpenDay: convertDayOfWeek(nextOpenDealerHour.dayOfWeek),
                openingTime: getTimeInDisplayFormat(nextOpen, locale),
            };
        }
        // dealer is not open now but will open at the same day
        return {
            isDealerOpen,
            openingTime: getTimeInDisplayFormat(open, locale),
            // open at the same day
            nextOpenDay: 0,
        };
    }
    // dealer is not scheduled to open today
    const nextOpenDealerHour = getNextOpenDealerHour(dealerHour, dealerHours);
    if (!nextOpenDealerHour)
        return null;
    const nextOpen = DateTime.fromISO(nextOpenDealerHour.start, { zone: dealerTimezone, locale });
    return {
        isDealerOpen: false,
        nextOpenDay: convertDayOfWeek(nextOpenDealerHour.dayOfWeek),
        openingTime: getTimeInDisplayFormat(nextOpen, locale),
    };
};
export default getDealerStatus;
